import { useState, useMemo } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { CaretUpDown, MagnifyingGlass } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

// Searchable checkbox multi-select for list filters. options: [{ value, label }], value: string[]
export default function MultiSelectFilter({ options = [], value = [], onChange, allLabel = 'All', noun = 'selected', testid, buttonClassName = '' }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const term = q.trim().toLowerCase();
  const filtered = useMemo(
    () => (term ? options.filter(o => `${o.label || ''} ${o.keywords || ''}`.toLowerCase().includes(term)) : options),
    [term, options],
  );
  const toggle = (v) => onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v]);
  const label = value.length === 0 ? allLabel
    : value.length === 1 ? (options.find(o => o.value === value[0])?.label || value[0])
    : `${value.length} ${noun}`;

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(''); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid}
          className={cn('inline-flex items-center justify-between gap-2 h-10 rounded-md border border-[#E2E8F0] bg-white px-3 text-sm text-left hover:border-[#0F4C81] transition-colors', value.length ? 'text-[#0F4C81] font-medium' : 'text-[#0F172A]', buttonClassName)}>
          <span className="truncate">{label}</span>
          <CaretUpDown size={14} className="shrink-0 text-[#64748B]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[260px]" align="start" data-testid={testid ? `${testid}-content` : undefined}>
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <MagnifyingGlass size={14} className="text-[#64748B]" />
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Type to search..."
            className="w-full text-sm outline-none bg-transparent" data-testid={testid ? `${testid}-search` : undefined} />
        </div>
        <div className="flex items-center justify-between px-3 py-1.5 border-b text-xs">
          <button type="button" className="text-[#0F4C81] hover:underline" onClick={() => onChange([])} data-testid={testid ? `${testid}-clear` : undefined}>Clear all</button>
          <button type="button" className="text-[#0F4C81] hover:underline"
            onClick={() => onChange(Array.from(new Set([...value, ...filtered.map(o => o.value)])))} data-testid={testid ? `${testid}-select-all` : undefined}>
            Select all{term ? ' shown' : ''} ({filtered.length})
          </button>
        </div>
        <div onWheel={(e) => { e.currentTarget.scrollTop += e.deltaY; }} className="max-h-64 overflow-y-auto py-1">
          {filtered.length === 0 && <div className="px-3 py-2 text-xs text-[#9CA3AF]">No match</div>}
          {filtered.map(o => (
            <label key={o.value} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-[#F1F5F9]"
              data-testid={testid ? `${testid}-opt-${String(o.value).replace(/\s+/g, '-')}` : undefined}>
              <input type="checkbox" className="accent-[#0F4C81]" checked={value.includes(o.value)} onChange={() => toggle(o.value)} />
              <span className="truncate">{o.label}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
